/**
 * 项目商品池 API 接口定义（MPM v5.0.0，【项目物料】-【项目商品池】页签）
 *
 * SCP 后端从登录态获取服务商并查询其生效履约项目，再转发 MPM（mdm-portal）按 SCP服务商 角色授权过滤；
 * 前端不传供应商标识。
 *
 * ======================== 接口总览 ========================
 * 1. GET /projectPool/projects - 【项目名称】下拉，附可见字段与导出权限
 * 2. GET /projectPool/page     - 项目商品池分页列表
 * 3. GET /projectPool/detail   - 项目商品池物料详情
 * 4. POST /projectPool/export  - 物料导出（进任务中心）
 * =========================================================
 */

import request from '../tools/request'
import { HTTP } from './http'
const { GET, POST } = request

// ====================== 数据类型定义 ======================

/** 【项目名称】下拉选项 */
export interface ProjectPoolProject {
  code: string // 项目编码
  name: string // 项目名称
  fields: string[] // SCP服务商 可见字段 code（与项目商品池返回字段名一致），按字典顺序
  exportEnabled: boolean // 是否允许导出
}

/** 销售区域&库存 */
export interface ProjectMaterialAreaInventory {
  area: string // 销售区域
  inventory: string // 库存
}

/** 项目商品池列表项（MPM ProjectMaterialPoolDTO，22 项业务字段 + 附加字段） */
export interface ProjectMaterialPoolDTO {
  id: string // 记录 id（同项目同咸亨编码固定不变）
  projectCode: string // 项目编码
  xhCode: string // 咸亨编码
  customerProductCode: string | null // 客户编码
  xhTitle: string | null // 咸亨标题
  brandName: string | null // 品牌名称
  customerCategoryName: string | null // 客户类目
  unitName: string | null // 单位名称
  projectPrice: number | null // 项目售价
  platformStatus: string | null // 平台状态：1已上架 2已下架 3审核中 4已驳回 5已冻结
  platformStatusName: string | null // 平台状态名称
  supplierName: string | null // 供应商名称
  factoryControl: boolean | null // 厂控物料：true是 false否 null未知
  actualSupply: string | null // 实供物料：1是 0否
  areaInventoryList: ProjectMaterialAreaInventory[] // 销售区域&库存，无数据为空数组
  multiplePurchaseQty: number | null // 成倍购买量
  minimumOrderQuantity: number | null // 起订量
  customerTitle: string | null // 客户标题
  controlPrice: number | null // 控价金额
  remark: string | null // 备注
  standardCode: string | null // 标准库编码/产品编码
  maxPrice: number | null // 最高可改价
  minPrice: number | null // 最低价
  salesVolume: string | null // 销量
  materialCode: string | null // 物资编码
  syncTime: number | null // 最近一次同步时间（毫秒）
  mainImageUrl: string | null // 首张主图完整 URL
}

/** 物料详情：列表全部字段 + 全部主图、详情图 */
export interface ProjectMaterialPoolDetailDTO extends ProjectMaterialPoolDTO {
  mainImageUrls: string[] // 全部主图完整 URL，无图为 []
  detailImageUrls: string[] // 全部详情图完整 URL，无图为 []
}

/** 分页结果 */
export interface ProjectMaterialPageResult {
  content: ProjectMaterialPoolDTO[]
  totalCount: number
  totalPages: number
  pageNo: number
  pageSize: number
}

/** 分页列表请求参数，排序固定为咸亨编码升序 */
export interface ProjectMaterialPageParams {
  projectCode: string // 项目编码
  pageNo: number // 页码，从 1 开始
  pageSize: number // 每页条数
  statusTab: string // 状态页签：ALL 全部 / ON_SHELF 已上架 / OTHER 其他状态
  xhCode?: string // 咸亨编码，精确
  customerTitle?: string // 客户标题，模糊
  brandName?: string // 品牌名称，精确
  customerCategoryName?: string // 客户类目，精确
  remark?: string // 备注，模糊
  standardCode?: string // 标准库编码/产品编码，精确
  materialCode?: string // 物资编码，精确
}

/** 物料导出请求参数：有勾选只导出勾选，否则导出当前项目、页签与筛选条件下全部数据 */
export interface ProjectPoolExportParams
  extends Omit<ProjectMaterialPageParams, 'pageNo' | 'pageSize'> {
  selectedIds: string[] // 当前页已勾选的记录 id，为空导出全部
}

// ====================== 接口方法 ======================

/** 【项目名称】下拉：当前服务商生效履约项目中 SCP服务商 角色授权开启的有效项目 */
export const GET_PROJECT_POOL_PROJECTS = () =>
  GET<ProjectPoolProject[]>(`${HTTP}/projectPool/projects`)

/** 项目商品池分页列表 */
export const GET_PROJECT_POOL_PAGE = (params: ProjectMaterialPageParams) =>
  GET<ProjectMaterialPageResult>(`${HTTP}/projectPool/page`, params)

/** 项目商品池物料详情 */
export const GET_PROJECT_POOL_DETAIL = (params: {
  projectCode: string
  xhCode: string
}) => GET<ProjectMaterialPoolDetailDTO>(`${HTTP}/projectPool/detail`, params)

/** 物料导出：校验通过后进任务中心异步生成 */
export const POST_PROJECT_POOL_EXPORT = (params: ProjectPoolExportParams) =>
  POST<boolean>(`${HTTP}/projectPool/export`, params, 'application/json')
/**
 * 项目商品池 API 接口定义（MPM v5.0.0，【项目物料】-【项目商品池】页签）
 *
 * SCP 后端从登录态获取服务商并查询其生效履约项目，再转发 MPM（mdm-portal）按 SCP服务商 角色授权过滤；
 * 前端不传供应商标识。
 *
 * ======================== 接口总览 ========================
 * 1. GET /projectPool/projects - 【项目名称】下拉，附可见字段与导出权限
 * 2. GET /projectPool/page     - 项目商品池分页列表
 * 3. GET /projectPool/detail   - 项目商品池物料详情
 * 4. POST /projectPool/export  - 物料导出（进任务中心）
 * =========================================================
 */

import request from '../tools/request'
import { HTTP } from './http'
const { GET, POST } = request

// ====================== 数据类型定义 ======================

/** 【项目名称】下拉选项 */
export interface ProjectPoolProject {
  code: string // 项目编码
  name: string // 项目名称
  fields: string[] // SCP服务商 可见字段 code（与项目商品池返回字段名一致），按字典顺序
  exportEnabled: boolean // 是否允许导出
}

/** 销售区域&库存 */
export interface ProjectMaterialAreaInventory {
  area: string // 销售区域
  inventory: string // 库存
}

/** 项目商品池列表项（MPM ProjectMaterialPoolDTO，22 项业务字段 + 附加字段） */
export interface ProjectMaterialPoolDTO {
  id: string // 记录 id（同项目同咸亨编码固定不变）
  projectCode: string // 项目编码
  xhCode: string // 咸亨编码
  customerProductCode: string | null // 客户编码
  xhTitle: string | null // 咸亨标题
  brandName: string | null // 品牌名称
  customerCategoryName: string | null // 客户类目
  unitName: string | null // 单位名称
  projectPrice: number | null // 项目售价
  platformStatus: string | null // 平台状态：1已上架 2已下架 3审核中 4已驳回 5已冻结
  platformStatusName: string | null // 平台状态名称
  supplierName: string | null // 供应商名称
  factoryControl: boolean | null // 厂控物料：true是 false否 null未知
  actualSupply: string | null // 实供物料：1是 0否
  areaInventoryList: ProjectMaterialAreaInventory[] // 销售区域&库存，无数据为空数组
  multiplePurchaseQty: number | null // 成倍购买量
  minimumOrderQuantity: number | null // 起订量
  customerTitle: string | null // 客户标题
  controlPrice: number | null // 控价金额
  remark: string | null // 备注
  standardCode: string | null // 标准库编码/产品编码
  maxPrice: number | null // 最高可改价
  minPrice: number | null // 最低价
  salesVolume: string | null // 销量
  materialCode: string | null // 物资编码
  syncTime: number | null // 最近一次同步时间（毫秒）
  mainImageUrl: string | null // 首张主图完整 URL
}

/** 物料详情：列表全部字段 + 全部主图、详情图 */
export interface ProjectMaterialPoolDetailDTO extends ProjectMaterialPoolDTO {
  mainImageUrls: string[] // 全部主图完整 URL，无图为 []
  detailImageUrls: string[] // 全部详情图完整 URL，无图为 []
}

/** 分页结果 */
export interface ProjectMaterialPageResult {
  content: ProjectMaterialPoolDTO[]
  totalCount: number
  totalPages: number
  pageNo: number
  pageSize: number
}

/** 分页列表请求参数，排序固定为咸亨编码升序 */
export interface ProjectMaterialPageParams {
  projectCode: string // 项目编码
  pageNo: number // 页码，从 1 开始
  pageSize: number // 每页条数
  statusTab: string // 状态页签：ALL 全部 / ON_SHELF 已上架 / OTHER 其他状态
  xhCode?: string // 咸亨编码，精确
  customerTitle?: string // 客户标题，模糊
  brandName?: string // 品牌名称，精确
  customerCategoryName?: string // 客户类目，精确
  remark?: string // 备注，模糊
  standardCode?: string // 标准库编码/产品编码，精确
  materialCode?: string // 物资编码，精确
}

/** 物料导出请求参数：有勾选只导出勾选，否则导出当前项目、页签与筛选条件下全部数据 */
export interface ProjectPoolExportParams
  extends Omit<ProjectMaterialPageParams, 'pageNo' | 'pageSize'> {
  selectedIds: string[] // 当前页已勾选的记录 id，为空导出全部
}

// ====================== 接口方法 ======================

/** 【项目名称】下拉：当前服务商生效履约项目中 SCP服务商 角色授权开启的有效项目 */
export const GET_PROJECT_POOL_PROJECTS = () =>
  GET<ProjectPoolProject[]>(`${HTTP}/projectPool/projects`)

/** 项目商品池分页列表 */
export const GET_PROJECT_POOL_PAGE = (params: ProjectMaterialPageParams) =>
  GET<ProjectMaterialPageResult>(`${HTTP}/projectPool/page`, params)

/** 项目商品池物料详情 */
export const GET_PROJECT_POOL_DETAIL = (params: {
  projectCode: string
  xhCode: string
}) => GET<ProjectMaterialPoolDetailDTO>(`${HTTP}/projectPool/detail`, params)

/** 物料导出：校验通过后进任务中心异步生成 */
export const POST_PROJECT_POOL_EXPORT = (params: ProjectPoolExportParams) =>
  POST<boolean>(`${HTTP}/projectPool/export`, params, 'application/json')
